// @ts-nocheck
import { dayjs } from 'utils/DateUtils';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { KTreeTableItem } from 'types/api/Tenant/AROKMS/KTreeDataTypes';
dayjs.extend(customParseFormat);

// Formats to try
const formats = ['DD-MM-YY', 'MMM-YY', 'YYYY/YY', 'MMM-MMM YYYY'];

// Get date from label
function getDateFromLabelTree(label) {
  // Only try to parse as date if the label looks like it might be a date
  // Check if it contains date-like patterns
  const datePatterns = [
    /^\d{2}-\d{2}-\d{2}$/, // DD-MM-YY for week
    /^[A-Za-z]{3}-\d{2}$/, // MMM-YY for month
    /^FY?\d{4}\/\d{2}$/, // [FY-]YYYY/YY for year
    /^[A-Za-z]{3}-[A-Za-z]{3}\s\d{4}$/, // MMM-MMM YYYY for quarter
  ];

  // Only try to parse as date if it matches one of our patterns
  for (const pattern of datePatterns) {
    if (pattern.test(label)) {
      // Special handling for quarter format
      if (pattern.source === /^[A-Za-z]{3}-[A-Za-z]{3}\s\d{4}$/.source) {
        // Extract the first month and year from quarter format (e.g., "Jan-Mar 2016" -> "Jan 2016")
        const match = label.match(/^([A-Za-z]{3})-[A-Za-z]{3}\s(\d{4})$/);
        if (match) {
          const month = match[1];
          const year = match[2];
          const quarterDate = dayjs(`${month} ${year}`, 'MMM YYYY');
          if (quarterDate.isValid()) {
            return quarterDate;
          }
        }
      } else {
        // Handle other date formats
        for (const format of formats) {
          const d = dayjs(label, format);
          if (d.isValid()) {
            return d;
          }
        }
      }
    }
  }

  // If no date pattern matches, return the original label
  return label;
}

// Recursive function to sort nested children
function sortChildren(node) {
  if (node.children && node.children.length > 0) {
    // Use standard Array.sort() with compareNodes for better performance
    node.children.sort(compareNodes);
    node.children.forEach(sortChildren);
  }
}

// helper to grab the first integer in a label (or null)
function getLeadingNumber(label) {
  // match any leading whitespace + digits
  const m = /^\s*(\d+)/.exec(label);
  return m ? parseInt(m[1], 10) : null;
}

function compareNodes(a, b) {
  // 1) Check if both are actual dates
  const dateA = getDateFromLabelTree(a.label);
  const dateB = getDateFromLabelTree(b.label);
  if (dayjs.isDayjs(dateA) && dayjs.isDayjs(dateB)) {
    if (dateA.isBefore(dateB)) return -1;
    if (dateA.isAfter(dateB)) return 1;
    return 0;
  }

  // 2) Try to sort on any leading number
  const numA = getLeadingNumber(a.label);
  const numB = getLeadingNumber(b.label);

  if (numA !== null || numB !== null) {
    // non-numbered labels go after numbered ones
    if (numA === null) {
      return 1;
    }
    if (numB === null) {
      return -1;
    }
    // different leading numbers
    if (numA !== numB) {
      const result = numA - numB;
      return result;
    }
    // same number—strip it off and compare the rest
    const restA = a.label.replace(/^\s*\d+/, '').trim();
    const restB = b.label.replace(/^\s*\d+/, '').trim();
    const result = restA.localeCompare(restB, 'en', {
      numeric: true,
      sensitivity: 'base',
    });
    return result;
  }

  // 3) No leading number at all—do a natural (numeric-aware) compare
  const result = a.label.localeCompare(b.label, 'en', {
    numeric: true,
    sensitivity: 'base',
  });
  return result;
}

// Sort function
export function sortTreeData(data) {
  if (Array.isArray(data)) {
    data.sort(compareNodes);
    data.forEach(sortChildren);
    return data;
  } else {
    return data.split('').sort().join('');
  }
}

export function convertObjectToValue(data, keyPair = 'value') {
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    if (typeof value === 'object' && value?.value && value?.label) {
      acc[key] = value[keyPair];
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function objectValidateWithSchema(object, schema) {
  return Object.keys(schema)
    .filter((key) => !schema[key](object[key]))
    .map((key) => new Error(`${key} is invalid.`));
}

export function isObjectEmptyValue(object) {
  return Object.keys(object).every((key) => !object[key]);
}

export function isObjectHasEmptyValue(object) {
  return Object.keys(object).some((key) => !object[key]);
}

export function createTreeData(parentName, data) {
  if (!parentName) {
    const highestLevel = data.reduce((acc, item) => {
      if (item.level > acc) {
        return item.level;
      } else {
        return acc;
      }
    }, 0);

    data = data.map((item) => {
      if (highestLevel !== 999 && item.level === highestLevel) {
        return {
          ...item,
          parent: null,
        };
      }
      return item;
    });
  }

  const children = data.filter((item) => item.parent === parentName);
  return children.map((child) => {
    if (child.parent === child.member) {
      return {
        label: child.member,
        level: child.level,
        value: child.id,
      };
    }
    return {
      label: child.member,
      value: child.id,
      level: child.level,
      children: createTreeData(child.member, data),
    };
  });
}

export function flattenTreeData(data, parent = null, level = 0) {
  let result = [];

  for (let item of data) {
    result.push({
      label: item.label.trim(),
      value: item.value,
      level: level,
      parent: parent?.trim(),
    });

    if (item.children && item.children.length) {
      result = result.concat(flattenTreeData(item.children, item.label, level + 1));
    }
  }

  return result;
}

export function renderOptionsKtree(props, option, state, lowestLevel) {
  if (option.level === lowestLevel) {
    return (
      <li
        {...props}
        style={{
          marginLeft: `${option.level * 3}px`,
          fontSize: '14px',
        }}
      >
        {option.label}
      </li>
    );
  }
  return (
    <li
      {...props}
      style={{
        fontWeight: 'bold',
        marginLeft: `${option.level * 2}px`,
        borderBottom: '1px solid #e3e3e3',
        fontSize: '14px',
      }}
    >
      {option.label}
    </li>
  );
}

type ListboxRef = (listboxNode: HTMLElement | null) => void;

// MUI re-attaches ListboxProps.ref whenever it gets a different function, and every re-attach re-syncs the highlight
// to the selected option, scrolling the open list back to it. Hand out the same ref for the same options and selection
// so a re-render of an open dropdown leaves the user's scroll position alone.
const listboxRefsByOptions = new WeakMap<AutoCompleteItem[], Map<AutoCompleteItem['value'] | undefined, ListboxRef>>();

export function getListBoxPropsAutoScrolItem(
  memberOptions: AutoCompleteItem[],
  selectedOption: AutoCompleteItem | null
) {
  let refsBySelectedValue = listboxRefsByOptions.get(memberOptions);
  if (!refsBySelectedValue) {
    refsBySelectedValue = new Map();
    listboxRefsByOptions.set(memberOptions, refsBySelectedValue);
  }

  const selectedValue = selectedOption?.value;
  let ref = refsBySelectedValue.get(selectedValue);
  if (!ref) {
    ref = (listboxNode: HTMLElement | null) => {
      if (listboxNode && listboxNode instanceof HTMLElement) {
        const index = memberOptions.findIndex((option: AutoCompleteItem) => option.value === selectedValue);
        if (index !== -1) {
          const itemNode = listboxNode.querySelector(`[data-option-index="${index}"]`);
          if (itemNode && itemNode instanceof HTMLElement) {
            listboxNode.scrollTop = itemNode.offsetTop;
          }
        }
      }
    };
    refsBySelectedValue.set(selectedValue, ref);
  }

  return { ref };
}

export function findObjectByLabelOrValue(text: string, options: DropdownItem[]) {
  const option = options.find((option) => option.label === text || option.value === text);
  return option;
}

export function generateUniqueLetter(records: any[], existingLetters: string[]) {
  const length = records.length - 1;

  function numToChars(num) {
    let chars = '';

    while (num >= 0) {
      chars = String.fromCharCode((num % 26) + 65) + chars;
      num = Math.floor(num / 26) - 1;
    }

    return chars;
  }

  function findUniqueChar(chars) {
    while (existingLetters.includes(chars)) {
      const index = (chars.charCodeAt(chars.length - 1) - 65 + 1) % 26;
      if (index === 0) {
        chars = chars.slice(0, -1) + String.fromCharCode(65) + (chars.length > 1 ? String.fromCharCode(65) : '');
      } else {
        chars = chars.slice(0, -1) + String.fromCharCode(index + 65);
      }
    }

    return chars;
  }

  const candidateChar = numToChars(length);
  return findUniqueChar(candidateChar);
}

export function normalizeDataGridRowId(rowId) {
  if (isNaN(rowId)) {
    return rowId;
  }
  return null;
}

export function sortAttributeColumnByOrderKtree(arr, map) {
  let sorted = [];

  function helper(item) {
    if (!arr.includes(item)) return;
    sorted.push(item);
    let children = [];
    for (let [key, value] of map) {
      if (value === item) children.push(key);
    }
    children.sort();
    for (let child of children) {
      helper(child);
    }
  }

  // Start from root element
  let root;
  for (let [key, value] of map) {
    if (value === null) {
      root = key;
      break;
    }
  }
  helper(root);

  return sorted;
}

export function getSortedKTreeId(arr, map, mapKTreeMemberWithId) {
  let sorted = [];

  function helper(item) {
    if (!arr.includes(item)) return;
    const id = mapKTreeMemberWithId.get(item);
    if (id) {
      sorted.push(id);
    }
    let children = [];
    for (let [key, value] of map) {
      if (value === item) children.push(key);
    }
    children.sort();
    for (let child of children) {
      helper(child);
    }
  }

  // Start from root element
  let root;
  for (let [key, value] of map) {
    if (value === null) {
      root = key;
      break;
    }
  }
  helper(root);
  return sorted;
}

export function mapKTreeMemberWithParent(ktreeData: KTreeTableItem[]) {
  const mapAttributeWithParent = new Map<string, string>();
  ktreeData.forEach((col) => {
    mapAttributeWithParent.set(col?.member, col?.parent);
  });
  return mapAttributeWithParent;
}

export function mapKTreeMemberWithId(ktreeData: KTreeTableItem[]) {
  const mapAttributeWithParent = new Map<string, string>();
  ktreeData.forEach((col) => {
    mapAttributeWithParent.set(col?.member, col?.id);
  });
  return mapAttributeWithParent;
}

export function isValidJSONString(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
